import { useEffect, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'
import {
    CartesianGrid,
    ComposedChart,
    Line,
    ReferenceLine,
    ResponsiveContainer,
    XAxis,
    YAxis,
} from 'recharts'
import type { DatedPoint, GraphApiResponse, GraphWidgetOptions } from './types'
import type { GraphWidgetViewState } from './GraphWidgetView'
import { buildCorrelationMatrix } from './correlation'

const DEFAULT_PAIR_ORDER = [
    'USDJPY', 'EURJPY', 'GBPJPY', 'AUDJPY', 'NZDJPY', 'CADJPY', 'CHFJPY',
    'EURUSD', 'GBPUSD', 'AUDUSD', 'NZDUSD', 'USDCAD', 'USDCHF',
    'AUDCAD', 'AUDNZD', 'NZDCAD',
    'XAUUSD', 'XAUJPY', 'XAGUSD',
]

export type CorrelationMatrixViewProps = {
    title?: string
    theme?: GraphWidgetOptions['theme']
    period?: GraphWidgetOptions['period']
    state: GraphWidgetViewState
}

function useResolvedTheme(theme: GraphWidgetOptions['theme']): 'dark' | 'light' {
    const [resolved, setResolved] = useState<'dark' | 'light'>(() => {
        if (theme === 'dark' || theme === 'light') return theme
        const prefersDark =
            typeof window !== 'undefined' &&
            typeof window.matchMedia === 'function' &&
            window.matchMedia('(prefers-color-scheme: dark)').matches
        return prefersDark ? 'dark' : 'light'
    })
    useEffect(() => {
        if (theme === 'dark' || theme === 'light') {
            setResolved(theme)
            return
        }
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
            setResolved('light')
            return
        }
        const mql = window.matchMedia('(prefers-color-scheme: dark)')
        const update = () => setResolved(mql.matches ? 'dark' : 'light')
        update()
        if (typeof mql.addEventListener === 'function') {
            mql.addEventListener('change', update)
            return () => mql.removeEventListener('change', update)
        }
        mql.addListener(update)
        return () => mql.removeListener(update)
    }, [theme])
    return resolved
}

function useViewportBreakpoint(breakpoint: number): boolean {
    const [matches, setMatches] = useState(() =>
        typeof window !== 'undefined' ? window.innerWidth < breakpoint : false,
    )
    useEffect(() => {
        if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return
        const mql = window.matchMedia(`(max-width: ${breakpoint - 1}px)`)
        const update = () => setMatches(mql.matches)
        update()
        if (typeof mql.addEventListener === 'function') {
            mql.addEventListener('change', update)
            return () => mql.removeEventListener('change', update)
        }
        mql.addListener(update)
        return () => mql.removeListener(update)
    }, [breakpoint])
    return matches
}

function useIsNarrow(breakpoint = 640): boolean {
    return useViewportBreakpoint(breakpoint)
}

function useIsMobile(breakpoint = 480): boolean {
    return useViewportBreakpoint(breakpoint)
}

function lerp(a: number, b: number, t: number): number {
    return a + (b - a) * t
}

function rgb(r: number, g: number, b: number): string {
    return `rgb(${Math.round(r)}, ${Math.round(g)}, ${Math.round(b)})`
}

function formatMonthDay(date: Date): string {
    return `${date.getMonth() + 1}/${date.getDate()}`
}

function formatHourMinute(date: Date): string {
    return `${date.getHours()}:${String(date.getMinutes()).padStart(2, '0')}`
}

function formatDateForPeriod(date: Date, period: GraphWidgetOptions['period']): string {
    if (period === 'day') {
        return `${formatMonthDay(date)} ${formatHourMinute(date)}`
    }
    return formatMonthDay(date)
}

function parseT(value: unknown): Date | null {
    if (typeof value !== 'string') return null
    const d = new Date(value)
    return Number.isNaN(d.getTime()) ? null : d
}

type SeqPoint = { t: string | number; y: number }

function extractPairSequence(raw: DatedPoint[] | number[] | undefined): SeqPoint[] {
    if (!Array.isArray(raw)) return []
    const out: SeqPoint[] = []
    raw.forEach((entry, i) => {
        if (typeof entry === 'number') {
            if (Number.isFinite(entry)) out.push({ t: i, y: entry })
            return
        }
        if (entry && typeof entry === 'object' && typeof entry.y === 'number' && Number.isFinite(entry.y)) {
            out.push({ t: typeof entry.t === 'string' ? entry.t : i, y: entry.y })
        }
    })
    return out
}

type ComparisonRow = { __index: number; t: string | number } & Record<string, number | string | null>

function buildComparisonSeries(
    data: GraphApiResponse | null,
    a: string,
    b: string,
): ComparisonRow[] {
    if (!data || !data.pairs) return []
    const seqA = extractPairSequence(data.pairs[a])
    const seqB = extractPairSequence(data.pairs[b])
    const n = Math.min(seqA.length, seqB.length)
    if (n < 2) return []

    let baseIdx = -1
    for (let i = 0; i < n; i++) {
        if (seqA[i].y > 0 && seqB[i].y > 0) {
            baseIdx = i
            break
        }
    }
    if (baseIdx < 0) return []

    const baseA = seqA[baseIdx].y
    const baseB = seqB[baseIdx].y

    const rows: ComparisonRow[] = []
    for (let i = baseIdx; i < n; i++) {
        const ay = seqA[i].y
        const by = seqB[i].y
        const row: ComparisonRow = {
            __index: i - baseIdx,
            t: seqA[i].t,
        }
        row[a] = Number.isFinite(ay) && ay > 0 ? (ay / baseA - 1) * 100 : null
        row[b] = Number.isFinite(by) && by > 0 ? (by / baseB - 1) * 100 : null
        rows.push(row)
    }
    return rows
}

function correlationColor(r: number, isDark: boolean): string {
    if (!Number.isFinite(r)) return isDark ? '#1f2937' : '#f3f4f6'
    const t = Math.max(-1, Math.min(1, r))
    const neutral = isDark ? [34, 34, 34] : [245, 245, 245]
    const red = isDark ? [150, 80, 92] : [235, 175, 185]
    const blue = isDark ? [80, 108, 150] : [175, 195, 225]
    if (t >= 0) {
        return rgb(
            lerp(neutral[0], red[0], t),
            lerp(neutral[1], red[1], t),
            lerp(neutral[2], red[2], t),
        )
    }
    const mag = -t
    return rgb(
        lerp(neutral[0], blue[0], mag),
        lerp(neutral[1], blue[1], mag),
        lerp(neutral[2], blue[2], mag),
    )
}

export function CorrelationMatrixView(props: CorrelationMatrixViewProps) {
    const { title = '通貨ペア相関', theme = 'auto', period, state } = props
    const resolvedTheme = useResolvedTheme(theme)
    const isNarrow = useIsNarrow()
    const isMobile = useIsMobile()
    const isDark = resolvedTheme === 'dark'

    const { keys, matrix } = useMemo(() => {
        if (state.status !== 'success' || !state.data) {
            return { keys: [] as string[], matrix: [] as number[][] }
        }
        return buildCorrelationMatrix(state.data.pairs ?? {}, DEFAULT_PAIR_ORDER)
    }, [state])

    const [selectedCell, setSelectedCell] = useState<{ a: string; b: string } | null>(null)

    useEffect(() => {
        if (!selectedCell) return
        if (!keys.includes(selectedCell.a) || !keys.includes(selectedCell.b)) {
            setSelectedCell(null)
        }
    }, [keys, selectedCell])

    useEffect(() => {
        if (!selectedCell) return
        const onKeyDown = (e: KeyboardEvent) => {
            if (e.key === 'Escape') setSelectedCell(null)
        }
        window.addEventListener('keydown', onKeyDown)
        return () => window.removeEventListener('keydown', onKeyDown)
    }, [selectedCell])

    const comparisonData = useMemo(() => {
        if (!selectedCell || state.status !== 'success') return []
        return buildComparisonSeries(state.data, selectedCell.a, selectedCell.b)
    }, [selectedCell, state])

    const selectedCorrelation = useMemo(() => {
        if (!selectedCell) return null
        const i = keys.indexOf(selectedCell.a)
        const j = keys.indexOf(selectedCell.b)
        if (i < 0 || j < 0) return null
        return matrix[i]?.[j] ?? null
    }, [selectedCell, keys, matrix])

    const palette = {
        panel: isDark ? '#0f172a' : '#ffffff',
        border: isDark ? 'rgba(148,163,184,0.18)' : '#e5e7eb',
        text: isDark ? '#e5e7eb' : '#111827',
        muted: isDark ? 'rgba(226,232,240,0.65)' : '#6b7280',
        gridBorder: isDark ? 'rgba(148,163,184,0.18)' : 'rgba(17,24,39,0.08)',
        headerBg: isDark ? '#0f172a' : '#ffffff',
        grid: isDark ? 'rgba(148,163,184,0.10)' : 'rgba(107,114,128,0.18)',
        axis: isDark ? 'rgba(226,232,240,0.55)' : 'rgba(17,24,39,0.55)',
    }

    const lineColors = isDark ? ['#38bdf8', '#f59e0b'] : ['#0284c7', '#d97706']

    const containerStyle: React.CSSProperties = {
        width: '100%',
        boxSizing: 'border-box',
        fontFamily:
            'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
        border: `1px solid ${palette.border}`,
        borderRadius: 14,
        padding: 12,
        background: palette.panel,
        color: palette.text,
    }

    const headerStyle: React.CSSProperties = {
        display: 'flex',
        alignItems: 'baseline',
        justifyContent: 'space-between',
        gap: 8,
        marginBottom: 8,
    }

    const titleStyle: React.CSSProperties = {
        fontSize: 'clamp(12px, 2.8vw, 14px)',
        fontWeight: 600,
        margin: 0,
    }

    const subTitleStyle: React.CSSProperties = {
        fontSize: 11,
        color: palette.muted,
        margin: 0,
        whiteSpace: 'nowrap',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 10,
    }

    const legendSwatchStyle: React.CSSProperties = {
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
    }

    const legendDotStyle = (bg: string): React.CSSProperties => ({
        width: 10,
        height: 10,
        borderRadius: 2,
        background: bg,
        border: `1px solid ${palette.gridBorder}`,
        display: 'inline-block',
    })

    if (state.status === 'loading' || state.status === 'idle') {
        return (
            <div style={containerStyle}>
                <div style={headerStyle}>
                    <p style={titleStyle}>{title}</p>
                </div>
                <div style={{ padding: 24, textAlign: 'center', color: palette.muted }}>Loading...</div>
            </div>
        )
    }
    if (state.status === 'error') {
        return (
            <div style={containerStyle}>
                <div style={headerStyle}>
                    <p style={titleStyle}>{title}</p>
                </div>
                <div style={{ padding: 24, textAlign: 'center', color: '#b91c1c' }}>
                    Failed to load: {state.message}
                </div>
            </div>
        )
    }
    if (keys.length === 0) {
        return (
            <div style={containerStyle}>
                <div style={headerStyle}>
                    <p style={titleStyle}>{title}</p>
                </div>
                <div style={{ padding: 24, textAlign: 'center', color: palette.muted }}>データがありません</div>
            </div>
        )
    }

    const cellFontSize = isNarrow ? 9 : 11
    const labelFontSize = isNarrow ? 9 : 11
    const cellPadding = isNarrow ? '3px 2px' : '5px 6px'

    const thBase: React.CSSProperties = {
        padding: cellPadding,
        border: `1px solid ${palette.gridBorder}`,
        background: palette.headerBg,
        fontSize: labelFontSize,
        fontWeight: 600,
        color: palette.muted,
        whiteSpace: 'nowrap',
    }

    return (
        <div style={containerStyle}>
            <div style={headerStyle}>
                <p style={titleStyle}>{title}</p>
                <p style={subTitleStyle}>
                    <span style={legendSwatchStyle}>
                        <span style={legendDotStyle(correlationColor(1, isDark))} />
                        赤: 正の相関
                    </span>
                    <span style={legendSwatchStyle}>
                        <span style={legendDotStyle(correlationColor(-1, isDark))} />
                        青: 負の相関
                    </span>
                </p>
            </div>
            {isMobile ? (
                <PairSelectorView
                    keys={keys}
                    matrix={matrix}
                    palette={palette}
                    isDark={isDark}
                    selectedCell={selectedCell}
                    onSelect={(a, b) => setSelectedCell({ a, b })}
                />
            ) : (
            <div style={{ overflowX: 'auto' }}>
                <table
                    style={{
                        borderCollapse: 'collapse',
                        fontSize: cellFontSize,
                        fontVariantNumeric: 'tabular-nums',
                        width: '100%',
                        minWidth: (keys.length + 1) * (isNarrow ? 40 : 56),
                    }}
                >
                    <thead>
                        <tr>
                            <th style={{ ...thBase, position: 'sticky', left: 0, zIndex: 2 }} />
                            {keys.map((k) => (
                                <th key={k} style={thBase}>{k}</th>
                            ))}
                        </tr>
                    </thead>
                    <tbody>
                        {keys.map((rowKey, i) => (
                            <tr key={rowKey}>
                                <th
                                    style={{
                                        ...thBase,
                                        textAlign: 'left',
                                        position: 'sticky',
                                        left: 0,
                                        zIndex: 1,
                                    }}
                                >
                                    {rowKey}
                                </th>
                                {keys.map((colKey, j) => {
                                    const r = matrix[i][j]
                                    const bg = correlationColor(r, isDark)
                                    const isDiagonal = i === j
                                    const isSelected =
                                        !!selectedCell &&
                                        ((selectedCell.a === rowKey && selectedCell.b === colKey) ||
                                            (selectedCell.a === colKey && selectedCell.b === rowKey))
                                    return (
                                        <td
                                            key={colKey}
                                            onClick={() => {
                                                if (isDiagonal) return
                                                setSelectedCell((prev) =>
                                                    prev && prev.a === rowKey && prev.b === colKey
                                                        ? null
                                                        : { a: rowKey, b: colKey },
                                                )
                                            }}
                                            style={{
                                                padding: cellPadding,
                                                border: isSelected
                                                    ? `2px solid ${palette.text}`
                                                    : `1px solid ${palette.gridBorder}`,
                                                background: bg,
                                                color: palette.text,
                                                textAlign: 'center',
                                                fontWeight: 600,
                                                whiteSpace: 'nowrap',
                                                cursor: isDiagonal ? 'default' : 'pointer',
                                                userSelect: 'none',
                                            }}
                                        >
                                            {Number.isFinite(r) ? r.toFixed(2) : '-'}
                                        </td>
                                    )
                                })}
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            )}
            {selectedCell && typeof document !== 'undefined'
                ? createPortal((() => {
                    const startDate = comparisonData.length > 0 ? parseT(comparisonData[0].t) : null
                    const endDate = comparisonData.length > 0 ? parseT(comparisonData[comparisonData.length - 1].t) : null
                    const startT = startDate ? formatDateForPeriod(startDate, period) : ''
                    const endT = endDate ? formatDateForPeriod(endDate, period) : ''
                    const xDomain: [number, number] = [0, Math.max(0, comparisonData.length - 1)]
                    return (
                        <div
                            onClick={() => setSelectedCell(null)}
                            style={{
                                position: 'fixed',
                                inset: 0,
                                background: 'rgba(0,0,0,0.45)',
                                zIndex: 9999,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                padding: isNarrow ? 12 : 24,
                                boxSizing: 'border-box',
                                fontFamily: 'ui-sans-serif, system-ui, -apple-system, Segoe UI, Roboto, Helvetica, Arial, "Apple Color Emoji", "Segoe UI Emoji"',
                            }}
                        >
                            <div
                                style={{
                                    width: '100%',
                                    maxWidth: 680,
                                    maxHeight: '90vh',
                                    overflowY: 'auto',
                                    padding: 16,
                                    borderRadius: 16,
                                    background: palette.panel,
                                    border: `1px solid ${palette.border}`,
                                    boxShadow: isDark
                                        ? '0 24px 60px rgba(0,0,0,0.6)'
                                        : '0 20px 50px rgba(17,24,39,0.18)',
                                    color: palette.text,
                                    boxSizing: 'border-box',
                                }}
                            >
                                <div
                                    style={{
                                        display: 'flex',
                                        alignItems: 'baseline',
                                        justifyContent: 'space-between',
                                        gap: 8,
                                        marginBottom: 8,
                                        flexWrap: 'wrap',
                                    }}
                                >
                                    <p style={{ margin: 0, fontSize: 'clamp(11px, 2.8vw, 14px)', fontWeight: 600 }}>
                                        {selectedCell.a} vs {selectedCell.b}
                                        {selectedCorrelation !== null && Number.isFinite(selectedCorrelation) ? (
                                            <span style={{ marginLeft: 10, fontSize: 11, color: palette.muted, fontWeight: 500 }}>
                                                相関係数 {selectedCorrelation.toFixed(2)}
                                            </span>
                                        ) : null}
                                    </p>
                                </div>
                                <div
                                    style={{
                                        width: '100%',
                                        height: isMobile ? 200 : 260,
                                        display: 'flex',
                                        flexDirection: isMobile ? 'column' : 'row',
                                        gap: isMobile ? 4 : 12,
                                    }}
                                >
                                    <div style={{ flex: 1, minWidth: 0, minHeight: 0 }}>
                                        <div style={{ position: 'relative', width: '100%', height: '100%' }}>
                                            <ResponsiveContainer>
                                                <ComposedChart
                                                    data={comparisonData}
                                                    margin={{ top: 8, right: 12, bottom: 20, left: 0 }}
                                                >
                                                    <CartesianGrid
                                                        stroke={palette.grid}
                                                        strokeWidth={0.5}
                                                        horizontal
                                                        vertical={false}
                                                    />
                                                    <ReferenceLine y={0} stroke={palette.axis} strokeDasharray="4 4" />
                                                    <XAxis
                                                        dataKey="__index"
                                                        type="number"
                                                        domain={xDomain}
                                                        hide
                                                        tick={false}
                                                        tickLine={false}
                                                        axisLine={false}
                                                    />
                                                    <YAxis
                                                        tick={false}
                                                        tickLine={false}
                                                        axisLine={false}
                                                        width={4}
                                                    />
                                                    <Line
                                                        type="linear"
                                                        dataKey={selectedCell.a}
                                                        stroke={lineColors[0]}
                                                        strokeWidth={2}
                                                        dot={false}
                                                        isAnimationActive={false}
                                                        connectNulls
                                                    />
                                                    <Line
                                                        type="linear"
                                                        dataKey={selectedCell.b}
                                                        stroke={lineColors[1]}
                                                        strokeWidth={2}
                                                        dot={false}
                                                        isAnimationActive={false}
                                                        connectNulls
                                                    />
                                                </ComposedChart>
                                            </ResponsiveContainer>
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    bottom: 0,
                                                    left: 0,
                                                    fontSize: 12,
                                                    lineHeight: 1,
                                                    color: palette.muted,
                                                }}
                                            >
                                                {startT}
                                            </div>
                                            <div
                                                style={{
                                                    position: 'absolute',
                                                    bottom: 0,
                                                    right: 12,
                                                    fontSize: 12,
                                                    lineHeight: 1,
                                                    color: palette.muted,
                                                }}
                                            >
                                                {endT}
                                            </div>
                                        </div>
                                    </div>
                                    <div
                                        style={
                                            isMobile
                                                ? {
                                                    display: 'flex',
                                                    flexDirection: 'row',
                                                    flexWrap: 'wrap',
                                                    justifyContent: 'center',
                                                    columnGap: 12,
                                                    rowGap: 4,
                                                    fontSize: 11,
                                                    color: palette.muted,
                                                }
                                                : {
                                                    width: 80,
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    justifyContent: 'center',
                                                    gap: 6,
                                                    fontSize: 12,
                                                    color: palette.muted,
                                                }
                                        }
                                    >
                                        {[selectedCell.a, selectedCell.b].map((k, idx) => (
                                            <div
                                                key={k}
                                                style={{ display: 'flex', alignItems: 'center', gap: isMobile ? 4 : 8, whiteSpace: 'nowrap' }}
                                            >
                                                <span
                                                    style={{
                                                        width: 10,
                                                        height: 10,
                                                        borderRadius: 2,
                                                        background: lineColors[idx],
                                                        display: 'inline-block',
                                                    }}
                                                />
                                                <span>{k}</span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                                <p style={{ margin: '6px 0 0', fontSize: 10, color: palette.muted }}>
                                    ※ 期間開始時点からの変動率 (%) で正規化して比較
                                </p>
                            </div>
                        </div>
                    )
                })(), document.body)
                : null}
        </div>
    )
}

type PairSelectorViewProps = {
    keys: string[]
    matrix: number[][]
    palette: {
        panel: string
        border: string
        text: string
        muted: string
        gridBorder: string
        headerBg: string
        grid: string
        axis: string
    }
    isDark: boolean
    selectedCell: { a: string; b: string } | null
    onSelect: (a: string, b: string) => void
}

function PairSelectorView(props: PairSelectorViewProps) {
    const { keys, matrix, palette, isDark, selectedCell, onSelect } = props

    const [basePair, setBasePair] = useState<string>(() => selectedCell?.a ?? keys[0] ?? '')

    useEffect(() => {
        if (keys.length === 0) return
        if (!keys.includes(basePair)) setBasePair(keys[0])
    }, [keys, basePair])

    if (keys.length === 0) {
        return <div style={{ fontSize: 11, color: palette.muted, padding: 8 }}>データがありません</div>
    }

    const baseIdx = keys.indexOf(basePair)
    const correlations = baseIdx >= 0
        ? keys
            .map((k, idx) => {
                if (idx === baseIdx) return null
                const r = matrix[baseIdx]?.[idx]
                if (typeof r !== 'number' || !Number.isFinite(r)) return null
                return { pair: k, r }
            })
            .filter((x): x is { pair: string; r: number } => x !== null)
            .sort((a, b) => b.r - a.r)
        : []

    const arrowColor = encodeURIComponent(palette.text)
    const caretSvg =
        `url("data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='12' height='8' viewBox='0 0 12 8'><path fill='none' stroke='${arrowColor}' stroke-width='2' stroke-linecap='round' stroke-linejoin='round' d='M1 1.5l5 5 5-5'/></svg>")`

    const selectStyle: React.CSSProperties = {
        width: '100%',
        boxSizing: 'border-box',
        padding: '10px 36px 10px 12px',
        fontSize: 14,
        fontWeight: 700,
        border: `1px solid ${palette.text}`,
        borderRadius: 10,
        background: `${palette.panel} ${caretSvg} no-repeat right 12px center`,
        backgroundSize: '12px 8px',
        color: palette.text,
        appearance: 'none',
        WebkitAppearance: 'none',
        MozAppearance: 'none',
        cursor: 'pointer',
        boxShadow: isDark
            ? '0 1px 0 rgba(255,255,255,0.04) inset, 0 2px 6px rgba(0,0,0,0.35)'
            : '0 1px 0 rgba(255,255,255,0.6) inset, 0 2px 6px rgba(17,24,39,0.08)',
    }

    return (
        <div>
            <label
                style={{
                    display: 'block',
                    fontSize: 11,
                    fontWeight: 600,
                    color: palette.muted,
                    marginTop: 4,
                    marginBottom: 6,
                }}
            >
                基準通貨ペア（タップで変更）
            </label>
            <select
                value={basePair}
                onChange={(e) => setBasePair(e.target.value)}
                style={selectStyle}
            >
                {keys.map((k) => (
                    <option key={k} value={k}>{k}</option>
                ))}
            </select>
            <div style={{ marginTop: 10 }}>
                {correlations.map((item) => {
                    const widthPct = Math.max(0, Math.min(1, Math.abs(item.r))) * 100
                    const valueText = `${item.r > 0 ? '+' : ''}${item.r.toFixed(2)}`
                    const isSelected =
                        !!selectedCell &&
                        ((selectedCell.a === basePair && selectedCell.b === item.pair) ||
                            (selectedCell.a === item.pair && selectedCell.b === basePair))
                    return (
                        <div
                            key={item.pair}
                            onClick={() => onSelect(basePair, item.pair)}
                            style={{
                                display: 'grid',
                                gridTemplateColumns: '64px minmax(0, 1fr) 44px',
                                alignItems: 'center',
                                columnGap: 8,
                                padding: '6px 4px',
                                borderTop: `1px solid ${palette.gridBorder}`,
                                cursor: 'pointer',
                                userSelect: 'none',
                                background: isSelected ? palette.headerBg : 'transparent',
                            }}
                        >
                            <div
                                style={{
                                    fontSize: 12,
                                    fontWeight: 600,
                                    color: palette.text,
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                }}
                            >
                                {item.pair}
                            </div>
                            <div
                                style={{
                                    position: 'relative',
                                    height: 8,
                                    borderRadius: 4,
                                    background: palette.gridBorder,
                                    overflow: 'hidden',
                                }}
                            >
                                <div
                                    style={{
                                        position: 'absolute',
                                        top: 0,
                                        left: 0,
                                        bottom: 0,
                                        width: `${widthPct}%`,
                                        background: correlationColor(item.r, isDark),
                                    }}
                                />
                            </div>
                            <div
                                style={{
                                    fontSize: 12,
                                    fontWeight: 700,
                                    textAlign: 'right',
                                    color: palette.text,
                                    fontVariantNumeric: 'tabular-nums',
                                }}
                            >
                                {valueText}
                            </div>
                        </div>
                    )
                })}
                {correlations.length === 0 ? (
                    <div style={{ fontSize: 11, color: palette.muted, padding: 8 }}>
                        相関データがありません
                    </div>
                ) : null}
            </div>
        </div>
    )
}
